import { useId, type ComponentProps } from "react";
import type { Activity, CustomStatus, PresenceState } from "@atrium/shared";
import { cn } from "../lib/cn";
import { activityLabel, STATUS_LABELS, usePresence, type PresenceEntry } from "../lib/presence";
import { Avatar } from "./ui/Avatar";

/*
 * Presence is shown by shape as well as colour, so it reads without colour vision:
 *   online  filled dot     idle  crescent moon     dnd  dot with a bar     offline  hollow ring
 * The ring around the shape uses --presence-ring (defaults to the sidebar surface)
 * so it separates cleanly from the avatar on any background.
 */

const SIZES = { xs: 10, sm: 12, md: 14, lg: 18 } as const;
export type PresenceSize = keyof typeof SIZES;

export function PresenceIndicator({ status, size = "sm", className, label = true }: { status: PresenceState; size?: PresenceSize; className?: string; label?: boolean }) {
  const id = useId();
  const px = SIZES[size];
  const fill = status === "online" ? "var(--success)" : status === "idle" ? "var(--warning)" : status === "dnd" ? "var(--danger)" : "var(--fg-3)";
  return (
    <svg
      viewBox="0 0 16 16"
      width={px}
      height={px}
      role={label ? "img" : undefined}
      aria-label={label ? STATUS_LABELS[status] : undefined}
      aria-hidden={label ? undefined : true}
      className={cn("shrink-0", className)}
      data-presence={status}
    >
      <circle cx="8" cy="8" r="8" fill="var(--presence-ring, var(--sidebar, var(--bg-subtle)))" />
      {status === "online" && <circle cx="8" cy="8" r="5.5" fill={fill} />}
      {status === "offline" && <circle cx="8" cy="8" r="4.25" fill="none" stroke={fill} strokeWidth="2.5" />}
      {status === "idle" && (
        <>
          <mask id={`${id}m`}>
            <rect width="16" height="16" fill="white" />
            <circle cx="5.25" cy="5.25" r="4" fill="black" />
          </mask>
          <circle cx="8" cy="8" r="5.5" fill={fill} mask={`url(#${id}m)`} />
        </>
      )}
      {status === "dnd" && (
        <>
          <mask id={`${id}m`}>
            <rect width="16" height="16" fill="white" />
            <rect x="4.5" y="6.75" width="7" height="2.5" rx="1.25" fill="black" />
          </mask>
          <circle cx="8" cy="8" r="5.5" fill={fill} mask={`url(#${id}m)`} />
        </>
      )}
    </svg>
  );
}

/** Avatar with a live presence indicator in the corner. */
export function PresenceAvatar({
  userId,
  presence,
  size = "md",
  ...avatar
}: Omit<ComponentProps<typeof Avatar>, "online"> & { userId: string; presence?: PresenceEntry | { status: PresenceState } }) {
  const live = usePresence(presence ? null : userId);
  const status = (presence ?? live)?.status ?? "offline";
  const indicator: PresenceSize = size === "xl" || size === "2xl" ? "lg" : size === "lg" || size === "md" ? "md" : "sm";
  return (
    <span className="relative inline-flex shrink-0">
      <Avatar size={size} {...avatar} />
      <PresenceIndicator status={status} size={indicator} className={cn("absolute", size === "xl" || size === "2xl" ? "bottom-0.5 right-0.5" : "-bottom-0.5 -right-0.5")} />
    </span>
  );
}

/** "🎧 Deep work" or "Playing Chess"; nothing when neither is set. */
export function statusText(p: { customStatus: CustomStatus | null; activity: Activity | null } | undefined): { emoji: string | null; text: string } | null {
  if (!p) return null;
  if (p.customStatus && (p.customStatus.text || p.customStatus.emoji)) return { emoji: p.customStatus.emoji, text: p.customStatus.text };
  if (p.activity) return { emoji: null, text: activityLabel(p.activity) };
  return null;
}

/** One-line status under a name: custom status (emoji + text) or the current activity. */
export function StatusLine({ userId, presence, className, fallback }: { userId?: string; presence?: PresenceEntry; className?: string; fallback?: string }) {
  const live = usePresence(presence ? null : userId);
  const s = statusText(presence ?? live);
  if (!s) return fallback ? <span className={cn("block truncate text-xs text-fg-3", className)}>{fallback}</span> : null;
  return (
    <span className={cn("flex min-w-0 items-center gap-1 text-xs text-fg-3", className)}>
      {s.emoji && <span aria-hidden className="shrink-0 text-xs leading-none">{s.emoji}</span>}
      <span className="truncate">{s.text || (s.emoji ? "" : "")}</span>
    </span>
  );
}
